import { ArrowRight, Phone, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CategoryCard } from "../components/catalog/CategoryCard";
import { ProductCard } from "../components/catalog/ProductCard";
import { Seo } from "../components/Seo";
import { LoadingSpinner } from "../components/ui/LoadingSpinner";
import { OptimizedImage } from "../components/ui/OptimizedImage";
import { getCategories, getProducts, getSiteSettings } from "../services/catalogService";
import { seedSettings } from "../data/seedData";
import type { Category, Product, SiteSettings } from "../types/catalog";
import { getSeasonalOfferPrice } from "../utils/productPrice";
import { useInitialCatalog } from "../contexts/PublicCatalogContext";
import { absoluteUrl } from "../utils/siteUrl";

export function Home() {
  const initial = useInitialCatalog();
  const [categories, setCategories] = useState<Category[]>(initial?.categories ?? []);
  const [products, setProducts] = useState<Product[]>(initial?.products ?? []);
  const [settings, setSettings] = useState<SiteSettings>(initial?.settings ?? seedSettings);
  const [loading, setLoading] = useState(!initial);

  useEffect(() => {
    void Promise.all([getCategories(), getProducts(), getSiteSettings()]).then(([nextCategories, nextProducts, nextSettings]) => {
      setCategories(nextCategories);
      setProducts(nextProducts);
      setSettings(nextSettings);
      setLoading(false);
    });
  }, []);

  const featuredProducts = useMemo(() => products.filter((product) => product.featured).slice(0, 4), [products]);
  const seasonalProducts = useMemo(
    () => products.filter((product) => product.active && product.available && getSeasonalOfferPrice(product) !== null),
    [products]
  );

  return (
    <>
      <Seo
        title={`${settings.companyName} | Sri Lankan Groceries in the UK`}
        description="Taste the real Sri Lanka in the UK with authentic groceries, fragrant spices, snacks, tea, staples, and frozen favourites."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": absoluteUrl("/#organization"),
          name: settings.companyName,
          url: absoluteUrl("/"),
          logo: absoluteUrl(settings.logo),
          telephone: settings.phone,
          email: settings.email,
          description: settings.about
        }}
      />
      <main>
        <section className="relative overflow-hidden bg-ink text-white">
          <div className="absolute inset-0">
            <OptimizedImage
              src="https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1800&q=80"
              alt=""
              className="h-full w-full object-cover opacity-25"
              loading="eager"
              fetchPriority="high"
              sizes="100vw"
              widthHint={1600}
              widths={[800, 1200, 1600, 2000]}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/95 to-ink/70" />
          <div className="relative mx-auto grid min-h-[620px] max-w-7xl items-center gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-harvest">Sri Lankan groceries in the UK</p>
              <h1 className="mt-4 max-w-3xl text-4xl font-black leading-tight text-white drop-shadow-lg sm:text-5xl lg:text-6xl">{settings.companyName}</h1>
              <p className="mt-6 max-w-2xl text-lg font-medium leading-8 text-white drop-shadow">
                Get ready to taste the real Sri Lanka in the UK. From fragrant spices to delicious snacks, we bring the authentic flavours of home to every shelf and every kitchen.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/products" className="inline-flex items-center gap-2 rounded-md bg-harvest px-5 py-3 text-sm font-bold text-ink transition hover:bg-white">
                  View Products
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-md border border-white/40 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/10">
                  <Phone className="h-4 w-4" />
                  Contact Sales
                </Link>
              </div>
            </div>
            <div className="rounded-lg border border-harvest/40 bg-white p-5 text-ink shadow-soft">
              <p className="mb-4 text-sm font-bold uppercase tracking-wide text-berry">Authentic ranges</p>
              <div className="grid grid-cols-2 gap-3">
                {loading ? (
                  <div className="col-span-2 flex h-48 items-center justify-center rounded-md border border-slate-200 bg-white">
                    <LoadingSpinner label="Loading ranges..." />
                  </div>
                ) : null}
                {!loading ? categories.slice(0, 4).map((category) => (
                  <Link key={category.id} to={`/categories/${category.slug}`} className="overflow-hidden rounded-md border border-slate-200 bg-white text-ink shadow-sm">
                    <OptimizedImage src={category.image} alt={category.name} className="h-28 w-full object-cover" sizes="(min-width: 1024px) 170px, 50vw" widthHint={320} />
                    <span className="block px-3 py-2 text-sm font-bold">{category.name}</span>
                  </Link>
                )) : null}
              </div>
            </div>
          </div>
        </section>

        {settings.seasonalOffersEnabled && seasonalProducts.length > 0 ? (
          <section aria-labelledby="seasonal-offers-heading" className="border-y border-harvest/40 bg-gradient-to-br from-harvest/20 via-white to-berry/10">
            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
              <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
                <div>
                  <p className="inline-flex items-center gap-2 rounded-full bg-berry px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-white shadow-sm">
                    <Sparkles className="h-4 w-4" aria-hidden="true" />
                    Limited-time selection
                  </p>
                  <h2 id="seasonal-offers-heading" className="mt-3 text-3xl font-black sm:text-4xl">Seasonal Offers</h2>
                  <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
                    Discover our current seasonal picks, selected to bring even more flavour to your table.
                  </p>
                </div>
                <Link to="/products" className="inline-flex items-center gap-2 text-sm font-bold text-berry transition hover:text-ink">
                  Browse full catalogue
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {seasonalProducts.map((product) => (
                  <ProductCard key={product.id} product={product} category={categories.find((category) => category.id === product.categoryId)} />
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-leaf">Featured categories</p>
              <h2 className="mt-2 text-3xl font-black">Cook up a Sri Lankan storm</h2>
            </div>
            <Link to="/categories" className="inline-flex items-center gap-2 text-sm font-bold text-leaf">
              All categories
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {loading ? <div className="col-span-full"><LoadingSpinner label="Loading categories..." /></div> : null}
            {!loading ? categories.slice(0, 3).map((category) => (
              <CategoryCard key={category.id} category={category} />
            )) : null}
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-leaf">Featured products</p>
                <h2 className="mt-2 text-3xl font-black">Flavours of home</h2>
              </div>
              <Link to="/products" className="inline-flex items-center gap-2 text-sm font-bold text-leaf">
                Full catalogue
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {loading ? <div className="col-span-full"><LoadingSpinner label="Loading products..." /></div> : null}
              {!loading ? featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} category={categories.find((category) => category.id === product.categoryId)} />
              )) : null}
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_0.8fr] lg:px-8">
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-leaf">About the company</p>
            <h2 className="mt-2 text-3xl font-black">The warmth of Sri Lankan cuisine in every bite</h2>
            <p className="mt-4 max-w-3xl text-base leading-8 text-slate-600">{settings.about}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-bold">Contact information</h3>
            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="font-bold text-slate-500">Phone</dt>
                <dd className="text-slate-800">{settings.phone}</dd>
              </div>
              <div>
                <dt className="font-bold text-slate-500">Email</dt>
                <dd className="text-slate-800">{settings.email}</dd>
              </div>
              <div>
                <dt className="font-bold text-slate-500">WhatsApp</dt>
                <dd>
                  <a href={settings.whatsApp} target="_blank" rel="noreferrer" className="font-semibold text-leaf hover:text-ink">
                    Follow our WhatsApp channel
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        </section>
      </main>
    </>
  );
}
